import React, { useState, useEffect, useRef } from 'react';
import { userFeedbackManager, buildMailtoUrl, type UserFeedback } from '../../utils/userFeedback';
import styles from './FeedbackForm.module.css';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: UserFeedback['type'] | undefined;
  initialCategory?: string | undefined;
  initialError?: string | undefined;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError || '',
    category: initialCategory,
    priority: 'medium' as UserFeedback['priority'],
  });

  const feedbackTypes = [
    { value: 'bug_report', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'general_feedback', label: 'General Feedback' },
    { value: 'usability_issue', label: 'Usability Issue' },
    { value: 'performance_issue', label: 'Performance Issue' },
  ];

  const categories = ['general', 'ui_ux', 'detection_accuracy', 'export', 'performance', 'accessibility'];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError || '',
        category: initialCategory,
        priority: 'medium',
      });
      setSubmitted(false);
      setFeedbackId(null);
      setSubmitError(null);
      contentRef.current?.focus();
    }
  }, [isOpen, initialType, initialCategory, initialError]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, url: window.location.href };
      const id = userFeedbackManager.submitFeedback(payload);
      setFeedbackId(id);

      const mailtoUrl = buildMailtoUrl(payload);
      if (mailtoUrl) window.location.href = mailtoUrl;

      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div className={styles.overlay} role="presentation">
      <div ref={contentRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Send feedback" className={styles.dialog}>
        <div className={styles.body}>
          <div className={styles.dialogHeader}>
            <h2 className={styles.title}>Send Feedback</h2>
            <button onClick={onClose} aria-label="Close" className={styles.closeButton}>&times;</button>
          </div>

          {submitted ? (
            <div className={styles.successPane}>
              <p className={styles.successTitle}>Thank you!</p>
              <p className={styles.successHint}>Your mail client should have opened. Send the pre-filled email to complete.</p>
              {feedbackId && <p className={styles.successRef}>Ref: {feedbackId}</p>}
              <button onClick={onClose} className={styles.primaryButton}>Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="fb-type" className={styles.label}>Type</label>
                <select id="fb-type" className={styles.select} value={formData.type} onChange={e => handleChange('type', e.target.value)}>
                  {feedbackTypes.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                </select>
              </div>

              <div className={styles.field}>
                <label htmlFor="fb-category" className={styles.label}>Category</label>
                <select id="fb-category" className={styles.select} value={formData.category} onChange={e => handleChange('category', e.target.value)}>
                  {categories.map(c => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <span id="fb-priority-label" className={styles.label}>Priority</span>
                <div role="radiogroup" aria-labelledby="fb-priority-label" className={styles.priorityGroup}>
                  {priorities.map(p => (
                    <button key={p.value} type="button"
                      role="radio"
                      aria-checked={formData.priority === p.value}
                      onClick={() => handleChange('priority', p.value)}
                      className={`${styles.priorityButton} ${formData.priority === p.value ? styles.priorityButtonActive : ''}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="fb-title" className={styles.label}>Title *</label>
                <input id="fb-title" type="text" required value={formData.title} onChange={e => handleChange('title', e.target.value)} placeholder="Brief summary" className={styles.input} />
              </div>

              <div className={styles.field}>
                <label htmlFor="fb-description" className={styles.label}>Description *</label>
                <textarea id="fb-description" rows={5} required value={formData.description} onChange={e => handleChange('description', e.target.value)} placeholder="Please describe in detail..." className={styles.textarea} />
              </div>

              {submitError && (
                <div className={styles.errorBanner} role="alert">
                  {submitError}
                </div>
              )}

              <div className={styles.actions}>
                <button type="button" onClick={onClose} className={styles.secondaryButton}>Cancel</button>
                <button type="submit" disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()} className={styles.primaryButton}>
                  {isSubmitting ? 'Opening mail...' : 'Send Feedback'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export const FeedbackButton: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <button
    onClick={onOpen}
    className={styles.fab}
    aria-label="Send feedback"
    title="Send feedback"
  >
    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  </button>
);
